import { apiService, resolveApiBaseUrl } from './api'
import { getUserTimezone } from '@/utils/timezoneUtils'
import type { ApiResponse, PaginatedResponse } from '@/types/api'
import type { ProductSavedFilterApi, ProductSavedFilterPayload } from '@/types/productSavedFilter'

export interface PIMProduct {
  id: number
  uuid: string
  sku: string
  label?: string
  price: number
  cost?: number
  quantity: number
  category?: string
  brand?: string
  weight?: number
  dimensions?: {
    length?: string
    width?: string
    height?: string
  }
  images?: string[]
  thumbnail_url?: string // Full URL to thumbnail (if provided by API)
  thumbnail?: string | null
  image_gallery?: string
  /** Full HTTP(S) URLs for gallery files (ProductResource). */
  image_gallery_urls?: string[]
  image_gallery_url?: string
  /** When set, this gallery token is shown first (future manual primary); null = order from filenames (_01 first). */
  image_gallery_primary_token?: string | null
  signagent_status?: string
  assets?: string
  /** Full HTTP(S) URLs for asset files (ProductResource). */
  assets_urls?: string[]
  status: 'draft' | 'active' | 'inactive' | 'archived'
  uin?: string
  buy_sku?: string | null
  price_breaks?: string
  stock_1?: string
  stock_2?: string
  stock_3?: string
  stock_4?: string
  stock_5?: string
  stock_6?: string
  stock_7?: string
  stock_8?: string
  stock_9?: string
  stock_10?: string
  stock_11?: string
  stock_12?: string
  created_at?: string
  updated_at?: string
  /** Multi-select manufacturing sourcing flags; API returns JSON array. */
  product_sourcing?: string[] | null
  /** Multi-select fixed website flags; API returns JSON array. */
  added_to_websites?: string[] | null
  /** Additional catalog / PIM fields from ProductResource (optional on list vs detail). */
  marketplace?: string | null
  session?: string | null
  tree_note?: string | null
  standard_or_custom?: string | null
  /** STD-CUSTOM-WIRE-001 — JSON schema of per-order custom fields when Standard-Custom. */
  custom_fields_definition?: import('@/types/origin').PimCustomField[] | null
  components?: string | null
  hardware_information?: string | null
  amazon_price?: string | number | null
  dsp_price?: string | number | null
  three_p_price?: string | number | null
  walmart_sams_price?: string | number | null
  dhl_price?: string | number | null
  geodis_price?: string | number | null
  kenco_price?: string | number | null
  nfi_price?: string | number | null
  product_minimum?: string | number | null
  categories?: string | string[] | null
  design_location?: string | null
  status_for_qp?: string | null
  width?: string | number | null
  height?: string | number | null
  length?: string | number | null
  vendor_supplier?: string | null
  single_sided_or_double_sided?: string | null
  process?: string | null
  fold_catalogue?: string | null
  rounded_corners?: string | null
  section_finishing?: string | null
  finishing?: string | null
  finishing_reference?: string | null
  preferred_location?: string | null
  preferred_press?: string | null
  preferred_stock_size?: string | null
  installation_method?: string | null
  installation_notes?: string | null
  product_gallery_url?: string | null
}

export interface ProductFilters {
  search?: string
  category?: string
  brand?: string
  status?: string
  min_price?: number
  max_price?: number
  page?: number
  per_page?: number
  sort_by?: string
  sort_order?: 'asc' | 'desc'
  sort_direction?: 'asc' | 'desc'
}

export interface ProductCreateData {
  sku: string
  label?: string
  price: number
  cost?: number
  quantity: number
  category?: string
  brand?: string
  weight?: number
  dimensions?: {
    length?: string
    width?: string
    height?: string
  }
  status?: 'draft' | 'active' | 'inactive'
}

export interface ProductUpdateData extends Partial<ProductCreateData> {
  image_gallery?: string
  assets?: string
  [key: string]: any
}

export interface ExportArchiveEstimate {
  product_count: number
  media_file_count: number
  estimated_media_bytes: number
  includes_image_gallery: boolean
  includes_assets: boolean
}

/**
 * Queued export response interface
 */
export interface ExportArchiveQueuedResponse {
  status: 'queued'
  export_id: string
  message: string
  status_url: string
}

/**
 * Export status interface
 */
export interface ExportArchiveStatus {
  status: 'queued' | 'processing' | 'completed' | 'failed'
  user_id: number
  export_id: string
  message: string
  progress?: number
  download_url?: string
  filename?: string
  size_bytes?: number
  product_count?: number
  media_files_count?: number
  export_download_id?: string
  expires_at?: string
  error?: string
  created_at: string
  updated_at: string
}

/**
 * Response for GET export-archive/download-links (list of files for an export)
 */
export interface ExportDownloadLinksResponse {
  links: Array<{ token: string; filename: string }>
}

/** Max files for synchronous import; above this use queued (ZIP) flow. */
export const IMPORT_MEDIA_SYNC_MAX_FILES = 20

/** POST /products/import-media `workflow` (backend default `mixed`). */
export type ImportMediaWorkflow = 'images' | 'assets' | 'mixed'

/** Per-product breakdown from import-media API (images + assets). */
export interface ImportMediaProductResult {
  product_id: number | null
  sku: string | null
  label: string | null
  product_uuid: string | null
  added_images: string[]
  replaced_images: string[]
  uploaded_new_images: string[]
  skipped_files: Array<{ filename: string; reason: string }>
  added_assets: string[]
  replaced_assets: string[]
  deleted_old_images: boolean
  deleted_old_images_count: number
  deleted_old_image_filenames: string[]
  main_image_changed_to: string | null
}

/** Result data shape returned by sync and by completed queued import. */
export interface ImportMediaResult {
  total_files: number
  imported_images_count: number
  imported_assets_count: number
  skipped_files: Array<{ filename: string; reason: string }>
  summary?: {
    total: number
    images: number
    assets: number
    skipped: number
    images_added: number
    images_replaced: number
    images_uploaded_new: number
    assets_added: number
    assets_replaced: number
    workflow: string
    delete_old_images: boolean
  }
  products?: ImportMediaProductResult[]
  unmatched_skipped?: Array<{ filename: string; reason: string }>
  /** Present when saving product_events failed for one or more products (see server logs). */
  timeline_errors?: Array<{ product_id: number; event_type: string; error: string }>
}

/** Response from POST import-media/queued when job is queued (202). */
export interface ImportMediaQueuedResponse {
  status: 'queued'
  import_media_id: string
  message: string
  status_url: string
}

/** Response from POST import-media/queued when sync queue returns 200 completed. */
export interface ImportMediaQueuedCompletedResponse {
  status: 'completed'
  import_media_id: string
  message: string
  progress: number
  data: ImportMediaResult
  updated_at?: string
}

/** Response from GET import-media/status/{id}. */
export interface ImportMediaStatusResponse {
  status: 'processing' | 'completed' | 'failed' | 'not_found'
  import_media_id: string
  message: string
  progress?: number
  data?: ImportMediaResult
  error?: string
  updated_at?: string
}

export interface ProductTimelineUser {
  uuid: string
  name: string
  email?: string
}

export interface ProductTimelineEventProduct {
  uuid: string
  sku: string | null
  label: string | null
}

/** Structured diff for product_updated / bulk_updated / import_updated timeline events */
export interface ProductTimelineFieldChange {
  field: string
  /** Human label when provided by API (CSV import). */
  field_label?: string
  old_value?: string | number | boolean | null
  new_value?: string | number | boolean | null
}

/** `import_media` event metadata (ZIP / sync / archive media import). */
export interface ProductTimelineImportMediaSection {
  deleted_before_import?: string[]
  deleted_before_replace?: string[]
  added?: string[]
  replaced?: string[]
  primary_before?: string | null
  primary_after?: string | null
  deleted_before_import_count?: number
  deleted_before_replace_count?: number
  added_count?: number
  replaced_count?: number
}

/** Flat `import_images` metadata from `ProductImportMediaTimelineAggregator`. */
export interface ProductTimelineImportImagesMetadata {
  kind?: string
  workflow?: string
  sku?: string
  label?: string
  marketplace?: unknown
  deleted_old_images?: boolean
  deleted_count?: number
  deleted_filenames?: string[]
  deleted_filenames_total?: number
  added_count?: number
  added_filenames?: string[]
  added_filenames_total?: number
  replaced_count?: number
  replaced_filenames?: string[]
  replaced_filenames_total?: number
  uploaded_count?: number
  uploaded_filenames?: string[]
  uploaded_filenames_total?: number
  primary_before?: string | null
  primary_after?: string | null
  main_image_changed_to?: string | null
}

/** Flat `import_assets` metadata from `ProductImportMediaTimelineAggregator`. */
export interface ProductTimelineImportAssetsMetadata {
  kind?: string
  workflow?: string
  sku?: string
  label?: string
  marketplace?: unknown
  deleted_before_replace_count?: number
  deleted_filenames?: string[]
  deleted_filenames_total?: number
  added_count?: number
  added_filenames?: string[]
  added_filenames_total?: number
  replaced_count?: number
  replaced_filenames?: string[]
  replaced_filenames_total?: number
}

export interface ProductTimelineActor {
  uuid: string
  name: string
}

export interface ProductTimelineEvent {
  id: number
  uuid: string
  /** Null for catalog-level events (e.g. saved filter lists). */
  product_id: number | null
  /** Null for catalog-level events (e.g. saved filter lists). */
  product_uuid: string | null
  event_type: string
  description: string
  metadata?: Record<string, unknown> | null
  performed_by_uuid: string | null
  performed_by_email: string | null
  created_at: string
  performed_by?: ProductTimelineUser | null
  actor?: ProductTimelineActor | null
  impersonated_user?: ProductTimelineActor | null
  is_impersonated?: boolean
  actor_display_name?: string
  /** Omitted or null when the event is not tied to a single product. */
  product?: ProductTimelineEventProduct | null
}

export interface ProductTimelineResponse {
  success: boolean
  events: ProductTimelineEvent[]
  next_cursor?: string | null
  /** Offset pagination next page when global timeline `search` is active; otherwise null. */
  next_page?: number | null
  message?: string
  error?: string
}

export interface ProductActivityDashboardRow {
  event_uuid: string
  occurred_at: string
  event_type: string
  sku: string
  attribute_name: string
  old_value: string
  new_value: string
}

export interface ProductActivityDashboardResponse {
  success: boolean
  date: string
  marketplace: string | null
  marketplaces: string[]
  metrics: {
    products_created: number
    products_modified: number
    products_deleted: number
  }
  activity_rows: ProductActivityDashboardRow[]
}

export interface ProductSkuHistoryChangedBy {
  uuid?: string
  name?: string
  email?: string
}

export interface ProductSkuHistoryEntry {
  previous_sku: string
  created_at: string | null
  source: string
  changed_by: ProductSkuHistoryChangedBy | null
}

/** Filter shape for product exports (CSV / archive); kept in JSON body to avoid nginx 414 on long URIs. */
export type ProductExportFilters = ProductFilters & {
  advanced_filters?: Array<{ attribute: string; operator: string; value: unknown }>
  filter_logic?: 'AND' | 'OR'
  groups?: Array<{ filters: Array<{ attribute: string; operator: string; value?: unknown }> }>
  product_ids?: number[]
  product_uuids?: string[]
  exclude_ids?: number[]
}

/** Merge filter fields into a JSON body for `scope === 'filtered'`. */
function buildProductExportFilterFields(
  scope: 'all' | 'filtered',
  filters: ProductExportFilters
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (scope !== 'filtered') return out
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue
    if (key === 'advanced_filters' && Array.isArray(value)) {
      out[key] = value
    } else if (key === 'groups' && Array.isArray(value)) {
      out[key] = value
    } else if (key === 'filter_logic') {
      out[key] = value
    } else {
      out[key] = value
    }
  }
  return out
}

class ProductService {
  private readonly endpoint = '/products'

  /**
   * Get paginated list of products
   */
  async getProducts(filters?: ProductFilters): Promise<PaginatedResponse<PIMProduct>> {
    // Add cache-busting timestamp to ensure fresh data from Meilisearch
    const params = {
      ...filters,
      _t: Date.now() // Backend will ignore this parameter
    }
    return await apiService.getPaginated<PIMProduct>(this.endpoint, params)
  }

  /**
   * Get a single product by UUID
   */
  async getProduct(uuid: string, cacheBust: boolean = false): Promise<ApiResponse<PIMProduct>> {
    const url = `${this.endpoint}/${uuid}`
    // Add cache-busting query parameter if needed
    const finalUrl = cacheBust ? `${url}?_t=${Date.now()}` : url
    return await apiService.get<ApiResponse<PIMProduct>>(finalUrl, {
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    })
  }

  /**
   * Superseded SKUs for a product (newest first). Requires pim.products.view.
   */
  async getProductSkuHistory(uuid: string): Promise<{ data: ProductSkuHistoryEntry[] }> {
    return await apiService.get<{ data: ProductSkuHistoryEntry[] }>(`${this.endpoint}/${uuid}/sku-history`)
  }

  /**
   * Create a new product
   */
  async createProduct(data: ProductCreateData): Promise<ApiResponse<PIMProduct>> {
    return await apiService.post<ApiResponse<PIMProduct>>(this.endpoint, data)
  }

  /**
   * Update an existing product by UUID
   */
  async updateProduct(uuid: string, data: ProductUpdateData): Promise<ApiResponse<PIMProduct>> {
    return await apiService.put<ApiResponse<PIMProduct>>(`${this.endpoint}/${uuid}`, data)
  }

  /**
   * Delete a product by UUID
   */
  async deleteProduct(uuid: string): Promise<void> {
    await apiService.delete(`${this.endpoint}/${uuid}`)
  }

  /**
   * Soft-deleted products for Admin → Recover Data (super-admin).
   */
  async getTrashedProducts(params?: {
    search?: string
    page?: number
    per_page?: number
  }): Promise<{
    data: Array<{
      id: number
      uuid: string
      sku: string
      label?: string | null
      category?: string | null
      status?: string | null
      deleted_at: string | null
      deleted_by: string | null
    }>
    meta: {
      current_page: number
      last_page: number
      per_page: number
      total: number
    }
  }> {
    return await apiService.get(`${this.endpoint}/trashed`, { params })
  }

  /**
   * Restore a soft-deleted product (super-admin).
   */
  async restoreProduct(uuid: string): Promise<{ message: string; data: PIMProduct }> {
    return await apiService.post(`${this.endpoint}/${uuid}/restore`)
  }

  /**
   * Bulk delete products (by IDs only; backend expects product_ids).
   */
  async bulkDeleteProducts(ids: number[]): Promise<void> {
    await apiService.post(`${this.endpoint}/bulk-delete`, { product_ids: ids })
  }

  /**
   * Bulk delete: accepts either product_ids or filter_query + exclude_ids (select all filtered).
   */
  async bulkDelete(payload: {
    product_ids?: number[]
    filter_query?: Record<string, unknown>
    exclude_ids?: number[]
  }): Promise<{ message: string; deleted_count: number; resolved_count?: number; capped?: boolean }> {
    return await apiService.post<{ message: string; deleted_count: number; resolved_count?: number; capped?: boolean }>(
      `${this.endpoint}/bulk-delete`,
      payload
    )
  }

  /**
   * Bulk update: accepts either product_ids or filter_query + exclude_ids, plus updates object.
   */
  async bulkUpdate(payload: {
    product_ids?: number[]
    filter_query?: Record<string, unknown>
    exclude_ids?: number[]
    updates: Record<string, unknown>
  }): Promise<{ message: string; updated_count: number; resolved_count?: number; capped?: boolean }> {
    return await apiService.post<{ message: string; updated_count: number; resolved_count?: number; capped?: boolean }>(
      `${this.endpoint}/bulk-update`,
      payload
    )
  }

  /**
   * Update product status by UUID
   */
  async updateProductStatus(
    uuid: string,
    status: 'draft' | 'active' | 'inactive' | 'archived'
  ): Promise<ApiResponse<PIMProduct>> {
    return await apiService.patch<ApiResponse<PIMProduct>>(`${this.endpoint}/${uuid}/status`, { status })
  }

  /**
   * Upload product image by UUID (single file - deprecated, kept for backward compatibility)
   */
  async uploadProductImage(
    uuid: string,
    file: File,
    type: 'thumbnail' | 'featured_image' | 'image_gallery' = 'image_gallery',
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<{ url: string }>> {
    const formData = new FormData()
    formData.append('image', file)
    formData.append('type', type)

    // Use apiService's post method with upload progress
    // We need to access the axios instance for onUploadProgress
    const axiosInstance = (apiService as any).api
    const response = await axiosInstance.post(
      `${this.endpoint}/${uuid}/images`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent: any) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            onProgress(progress)
          }
        }
      }
    )

    return response.data
  }

  /**
   * Upload multiple product images (new multi-file method)
   */
  async uploadProductImages(
    uuid: string,
    files: File[],
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<{ uploaded: Array<{ url: string; path: string; original_name: string; mime: string; size: number }>; total: number }>> {
    const formData = new FormData()
    // Use explicit indices (images[0], images[1], etc.) for better Laravel compatibility
    files.forEach((file, index) => {
      formData.append(`images[${index}]`, file)
    })

    const axiosInstance = (apiService as any).api
    const response = await axiosInstance.post(
      `${this.endpoint}/${uuid}/images`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent: any) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            onProgress(progress)
          }
        }
      }
    )

    return response.data
  }

  /**
   * Delete individual product image
   */
  async deleteProductImage(
    uuid: string,
    filename: string
  ): Promise<ApiResponse> {
    return await apiService.delete(`${this.endpoint}/${uuid}/images`, {
      params: { filename }
    })
  }

  /**
   * Upload multiple product assets (PDFs, DOCs, SVGs, etc.)
   */
  async uploadProductAssets(
    uuid: string,
    files: File[],
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<{ uploaded: Array<{ url: string; path: string; original_name: string; mime: string; size: number }>; total: number }>> {
    const formData = new FormData()
    files.forEach((file) => {
      formData.append('assets[]', file)
    })

    const axiosInstance = (apiService as any).api
    const response = await axiosInstance.post(
      `${this.endpoint}/${uuid}/assets`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 300000, // 5 minutes for asset uploads (larger files can take time)
        onUploadProgress: (progressEvent: any) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            onProgress(progress)
          }
        }
      }
    )

    return response.data
  }

  /**
   * Delete individual product asset
   */
  async deleteProductAsset(
    uuid: string,
    filename: string
  ): Promise<ApiResponse> {
    return await apiService.delete(`${this.endpoint}/${uuid}/assets`, {
      params: { filename }
    })
  }

  /**
   * Get product categories
   */
  async getCategories(): Promise<ApiResponse<string[]>> {
    // API returns { data: [...] }, apiService.get returns response.data which is { data: [...] }
    const response = await apiService.get<{ data: string[] }>(`${this.endpoint}/categories`)
    // Extract the actual array from { data: [...] }
    return {
      data: (response && response.data) ? response.data : [],
      status: 200
    }
  }

  /**
   * Get product brands
   */
  async getBrands(): Promise<ApiResponse<string[]>> {
    const response = await apiService.get<{ data: string[] } | string[]>(`${this.endpoint}/brands`)
    const brands = Array.isArray(response) ? response : (response?.data ?? [])
    return { data: brands, status: 200 }
  }

  async getMarketplaces(): Promise<ApiResponse<string[]>> {
    const response = await apiService.get<{ data: string[] }>(`${this.endpoint}/marketplaces`)
    return {
      data: (response && response.data) ? response.data : [],
      status: 200
    }
  }

  /**
   * Product Development activity dashboard: timeline metrics and rows for one calendar day.
   */
  async getActivityDashboard(params: {
    date: string
    marketplace?: string | null
  }): Promise<ProductActivityDashboardResponse> {
    const q = new URLSearchParams()
    q.set('date', params.date)
    if (params.marketplace != null && String(params.marketplace).trim() !== '') {
      q.set('marketplace', String(params.marketplace).trim())
    }
    return await apiService.get<ProductActivityDashboardResponse>(
      `${this.endpoint}/activity-dashboard?${q.toString()}`
    )
  }

  async getTreeNotes(): Promise<ApiResponse<string[]>> {
    const response = await apiService.get<{ data: string[] }>(`${this.endpoint}/tree-notes`)
    return { data: (response && response.data) ? response.data : [], status: 200 }
  }

  async getAddedToWebsites(): Promise<ApiResponse<string[]>> {
    const response = await apiService.get<{ data: string[] }>(`${this.endpoint}/added-to-websites`)
    return { data: (response && response.data) ? response.data : [], status: 200 }
  }

  async getFinishing(): Promise<ApiResponse<string[]>> {
    const response = await apiService.get<{ data: string[] }>(`${this.endpoint}/finishing`)
    return { data: (response && response.data) ? response.data : [], status: 200 }
  }

  async getFinishingReference(): Promise<ApiResponse<string[]>> {
    const response = await apiService.get<{ data: string[] }>(`${this.endpoint}/finishing-reference`)
    return { data: (response && response.data) ? response.data : [], status: 200 }
  }

  async getFoldCatalogue(): Promise<ApiResponse<string[]>> {
    const response = await apiService.get<{ data: string[] }>(`${this.endpoint}/fold-catalogue`)
    return { data: (response && response.data) ? response.data : [], status: 200 }
  }

  async getSectionFinishing(): Promise<ApiResponse<string[]>> {
    const response = await apiService.get<{ data: string[] }>(`${this.endpoint}/section-finishing`)
    return { data: (response && response.data) ? response.data : [], status: 200 }
  }

  async getSession(): Promise<ApiResponse<string[]>> {
    const response = await apiService.get<{ data: string[] }>(`${this.endpoint}/session`)
    return { data: (response && response.data) ? response.data : [], status: 200 }
  }

  /**
   * Quick search products (for autocomplete/typeahead).
   * Returns limited results for real-time search while typing.
   */
  async quickSearch(query: string, limit: number = 10): Promise<ApiResponse<PIMProduct[]>> {
    if (!query || query.length < 2) {
      return { data: [], status: 200 }
    }
    
    const response = await apiService.get<{ data: PIMProduct[] }>(`${this.endpoint}/quick-search`, {
      params: { q: query, limit: Math.min(limit, 20) }
    })
    
    return {
      data: (response && response.data) ? response.data : [],
      status: 200
    }
  }

  /**
   * Get products with advanced filtering (attribute-based with operators)
   * Supports grouped filters (groups) and legacy (advanced_filters + filter_logic).
   * Only available for PIM module
   */
  async getProductsWithAdvancedFilters(filters: ProductFilters & {
    advanced_filters?: Array<{
      attribute: string
      operator: string
      value: any
    }>
    filter_logic?: 'AND' | 'OR'
    /** Grouped filters: AND within each group, OR between groups (Plytix-style) */
    groups?: Array<{ filters: Array<{ attribute: string; operator: string; value?: unknown }> }>
  }): Promise<PaginatedResponse<PIMProduct>> {
    const params: Record<string, unknown> = {
      ...filters,
      _t: Date.now()
    }
    // Serialize groups for query string (backend expects JSON string or array)
    if (params.groups && Array.isArray(params.groups)) {
      params.groups = JSON.stringify(params.groups)
    }
    const res = await apiService.getPaginated<PIMProduct>(`${this.endpoint}/advanced-filter`, params)
    return res
  }

  /**
   * Get available product attributes for filtering
   */
  async getAvailableAttributes(): Promise<ApiResponse<Array<{ value: string; label: string }>>> {
    const response = await apiService.get<{ data: Array<{ value: string; label: string }> }>(
      `${this.endpoint}/available-attributes`
    )
    return {
      data: (response && response.data) ? response.data : [],
      status: 200
    }
  }

  /**
   * Get distinct attribute values for suggestions (bulk edit, filters).
   * @param attribute Canonical name (e.g. "Material") or column key (e.g. "attribute_material")
   */
  async getAttributeValues(attribute: string, q?: string, limit?: number): Promise<ApiResponse<string[]>> {
    const params: Record<string, string | number> = { attribute }
    if (q != null && q !== '') params.q = q
    if (limit != null) params.limit = limit
    const response = await apiService.get<{ data: string[] }>(`${this.endpoint}/attribute-values`, { params })
    return {
      data: (response?.data && Array.isArray(response.data)) ? response.data : [],
      status: 200
    }
  }

  /**
   * Export products to CSV
   * 
   * @param scope 'all' to export all products, 'filtered' to export filtered products
   * @param filters Filter conditions (only used if scope='filtered')
   * @param selectedFields Fields to include in export
   */
  async exportCsv(
    scope: 'all' | 'filtered' = 'all',
    filters: ProductExportFilters = {},
    selectedFields: string[] = [],
    includeImageGallery: boolean = false,
    includeAssets: boolean = false
  ): Promise<void> {
    const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api'
    const url = `${apiBaseUrl}${this.endpoint}/export-csv`

    const body = {
      scope,
      selected_fields: selectedFields,
      include_image_gallery: includeImageGallery,
      include_assets: includeAssets,
      ...buildProductExportFilterFields(scope, filters)
    }

    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token')

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'text/csv, application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    })

    if (!response.ok) {
      const errorText = await response.text()
      let errorMessage = 'Failed to export products'
      try {
        const parsed = JSON.parse(errorText) as { message?: string }
        if (parsed?.message) errorMessage = parsed.message
      } catch {
        // non-JSON (e.g. nginx 414 HTML)
      }
      if (response.status === 414 || /414|Request-URI Too Large/i.test(errorText)) {
        throw new Error(
          'This export uses too many filter values for one request. Try fewer values (for example fewer SKUs), narrow the filter, or contact support.'
        )
      }
      const err = new Error(errorMessage) as Error & { status?: number }
      err.status = response.status
      throw err
    }

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition')
    
    // Generate filename with RUKHERP_YYYY_MM_DD_ pattern
    const date = new Date()
    const dateStr = `${date.getFullYear()}_${String(date.getMonth() + 1).padStart(2, '0')}_${String(date.getDate()).padStart(2, '0')}`
    const timeStr = `${String(date.getHours()).padStart(2, '0')}${String(date.getMinutes()).padStart(2, '0')}${String(date.getSeconds()).padStart(2, '0')}`
    const scopeLabel = scope === 'all' ? 'all' : 'filtered'
    let filename = `RUKHERP_${dateStr}_products_export_${scopeLabel}_${timeStr}.csv` // Fallback
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch && filenameMatch[1]) {
        // Remove quotes if present
        filename = filenameMatch[1].replace(/['"]/g, '')
      }
    }

    const blob = await response.blob()
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  }


  /**
   * Get ZIP import template (CSV + example folders)
   */
  async downloadImportTemplate(): Promise<void> {
    const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api'
    const url = `${apiBaseUrl}${this.endpoint}/import-template`
    
    // Get auth token
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token')
    
    // Download CSV file (no longer returns ZIP)
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'text/csv'
      }
    })

    if (!response.ok) {
      throw new Error('Failed to download template')
    }

    // Extract filename from Content-Disposition header or generate with RUKHERP_ prefix
    const contentDisposition = response.headers.get('Content-Disposition')
    const date = new Date()
    const dateStr = `${date.getFullYear()}_${String(date.getMonth() + 1).padStart(2, '0')}_${String(date.getDate()).padStart(2, '0')}`
    let filename = `RUKHERP_${dateStr}_products_import_template.csv` // Fallback
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch && filenameMatch[1]) {
        // Remove quotes if present
        filename = filenameMatch[1].replace(/['"]/g, '')
      }
    }

    const blob = await response.blob()
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  }

  /**
   * Pre-flight estimate for ZIP archive export with media files.
   */
  async estimateArchiveExport(
    scope: 'all' | 'filtered' = 'all',
    filters: ProductExportFilters = {},
    includeImageGallery: boolean = false,
    includeAssets: boolean = false
  ): Promise<ExportArchiveEstimate> {
    const requestBody = {
      scope,
      include_image_gallery: includeImageGallery,
      include_assets: includeAssets,
      ...buildProductExportFilterFields(scope, filters)
    }

    return apiService.post<ExportArchiveEstimate>(
      `${this.endpoint}/export-archive/estimate`,
      requestBody
    )
  }

  /**
   * Export products as ZIP archive (CSV + media files) - Synchronous
   * NOTE: This method is kept for backward compatibility but should not be used for ZIP exports with media.
   * Use exportArchiveQueued() instead for ZIP exports to avoid HTTP timeouts.
   */
  async exportArchive(
    scope: 'all' | 'filtered' = 'all',
    filters: ProductExportFilters = {},
    selectedFields: string[] = [],
    includeMedia: boolean = true,
    includeImageGallery: boolean = false,
    includeAssets: boolean = false
  ): Promise<{ download_url: string; filename: string; size_bytes: number; product_count: number; media_files_count: number }> {
    const requestBody = {
      scope,
      selected_fields: selectedFields,
      include_media: includeMedia,
      include_image_gallery: includeImageGallery,
      include_assets: includeAssets,
      ...buildProductExportFilterFields(scope, filters)
    }

    const axiosInstance = (apiService as any).api
    const response = await axiosInstance.post(
      `${this.endpoint}/export-archive`,
      requestBody,
      {
        timeout: 1800000 // 30 minutes for large file uploads (5GB)
      }
    )

    return response.data
  }

  /**
   * Start a queued export (for ZIP archives with media files)
   * Returns immediately with export_id, requires polling for status
   */
  async exportArchiveQueued(
    scope: 'all' | 'filtered' = 'all',
    filters: ProductExportFilters = {},
    selectedFields: string[] = [],
    includeMedia: boolean = true,
    includeImageGallery: boolean = false,
    includeAssets: boolean = false
  ): Promise<ExportArchiveQueuedResponse> {
    const requestBody = {
      scope,
      selected_fields: selectedFields,
      include_media: includeMedia,
      include_image_gallery: includeImageGallery,
      include_assets: includeAssets,
      ...buildProductExportFilterFields(scope, filters)
    }

    const response = await apiService.post<unknown>(
      `${this.endpoint}/export-archive/queued`,
      requestBody
    )

    if (typeof response === 'string') {
      if (/414|Request-URI Too Large/i.test(response)) {
        throw new Error(
          'This export uses too many filter values for one request. Try fewer values (for example fewer SKUs), narrow the filter, or export as CSV without media.'
        )
      }
      throw new Error('Invalid export response from server.')
    }
    if (
      !response ||
      typeof response !== 'object' ||
      !('export_id' in response) ||
      !(response as ExportArchiveQueuedResponse).export_id
    ) {
      throw new Error('Invalid export response: missing export_id')
    }
    return response as ExportArchiveQueuedResponse
  }

  /**
   * Check export status
   */
  async getExportArchiveStatus(exportId: string): Promise<ExportArchiveStatus> {
    // apiService.get already returns response.data from axios
    // Backend returns the object directly (not wrapped in { data: ... })
    const response = await apiService.get<ExportArchiveStatus>(
      `${this.endpoint}/export-archive/status/${exportId}`
    )
    
    return response
  }

  /**
   * Get list of download links for an export (for the email-link download page)
   */
  async getExportDownloadLinks(exportId: string): Promise<ExportDownloadLinksResponse> {
    const response = await apiService.get<ExportDownloadLinksResponse>(
      `${this.endpoint}/export-archive/download-links?export=${encodeURIComponent(exportId)}`
    )
    return response
  }

  /**
   * Download export file
   * Handles large files, inactive tabs, and provides fallbacks
   */
  async downloadExportArchive(downloadUrl: string, filename: string): Promise<void> {
    console.log('[Download] Starting download:', { downloadUrl, filename })
    
    // downloadUrl is already a full path like /api/products/export-archive/download/{token}
    // Get auth token
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token')
    
    if (!token) {
      console.error('[Download] No auth token found')
      throw new Error('Authentication required')
    }
    
    // Remove /api prefix if present since downloadUrl already includes it
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api'
    const apiBaseUrl = baseUrl.replace(/\/api$/, '')
    const fullUrl = `${apiBaseUrl}${downloadUrl}`
    
    console.log('[Download] Full URL:', fullUrl)
    
    // Check if tab is visible/active - browsers block downloads in inactive tabs
    const isTabVisible = !document.hidden && document.hasFocus()
    console.log('[Download] Tab visibility:', { hidden: document.hidden, hasFocus: document.hasFocus(), isTabVisible })
    
    // If tab is inactive, log a warning but still attempt download
    // The polling composable will handle retry if this fails
    if (!isTabVisible) {
      console.warn('[Download] Tab is inactive - download may be blocked by browser security policies')
    }
    
    try {
      console.log('[Download] Fetching file...')
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/zip'
        }
      })
      
      console.log('[Download] Response status:', response.status, response.statusText)
      
      if (!response.ok) {
        const errorText = await response.text()
        console.error('[Download] Response error:', errorText)
        throw new Error(`Failed to download ZIP archive: ${response.status} ${response.statusText}`)
      }
      
      // Check content length for logging
      const contentLength = response.headers.get('content-length')
      const fileSize = contentLength ? parseInt(contentLength, 10) : null
      const fileSizeGB = fileSize ? (fileSize / 1024 / 1024 / 1024).toFixed(2) : 'unknown'
      console.log('[Download] File size:', fileSize, 'bytes (', fileSizeGB, 'GB)')
      
      // For large files, modern browsers handle blob creation efficiently
      // The browser may use disk space instead of RAM for very large blobs
      console.log('[Download] Creating blob from response...')
      const blob = await response.blob()
      console.log('[Download] Blob created, size:', blob.size, 'bytes')
      
      const url = window.URL.createObjectURL(blob)
      console.log('[Download] Object URL created')
      
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.style.display = 'none'
      document.body.appendChild(link)
      console.log('[Download] Triggering click...')
      link.click()
      console.log('[Download] Click triggered')
      
      // Clean up after a delay
      setTimeout(() => {
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        console.log('[Download] Cleanup completed')
      }, 100)
    } catch (error: any) {
      console.error('[Download] Error during download:', error)
      console.error('[Download] Error details:', {
        message: error?.message,
        stack: error?.stack,
        name: error?.name
      })
      
      // If download failed and tab is inactive, the retry mechanism will handle it
      // Otherwise, rethrow the error
      throw error
    }
  }

  /**
   * Import products from ZIP or CSV archive
   * 
   * Backend endpoint: POST /api/products/import-archive
   * Backend expects:
   * - file: File (required, ZIP or CSV)
   * - upsert: boolean (default: true) - sent as query param 'upsert'
   * - overwrite_media: boolean (default: true) - sent as query param 'overwrite_media'
   * 
   * NOTE: Backend accepts both ZIP and CSV files via this endpoint.
   * The backend determines file type automatically.
   */
  async importArchive(
    file: File,
    upsert: boolean = true,
    overwriteMedia: boolean = true
  ): Promise<ApiResponse<{
    created: number
    updated: number
    skipped: number
    errors: string[]
    media: {
      copied: number
      overwritten: number
      missing: number
      skipped: number
    }
    warnings: string[]
  }>> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('upsert', String(upsert))
    formData.append('overwrite_media', String(overwriteMedia))

    const axiosInstance = (apiService as any).api
    const response = await axiosInstance.post(
      `${this.endpoint}/import-archive`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 1800000 // 30 minutes for large file uploads (5GB)
      }
    )

    return response.data
  }

  /**
   * Preview CSV import: returns unknown option values per attribute and row count (no DB writes).
   * Backend: POST /api/products/import-csv-preview
   */
  async importCsvPreview(file: File): Promise<ApiResponse<{
    unknown_marketplaces: string[]
    unknown_tree_notes: string[]
    unknown_finishing: string[]
    unknown_finishing_reference: string[]
    unknown_fold_catalogue: string[]
    unknown_section_finishing: string[]
    unknown_session: string[]
    invalid_standard_or_custom?: string[]
    invalid_design_location?: string[]
    invalid_status_for_qp?: string[]
    invalid_signagent_status?: string[]
    invalid_status?: string[]
    invalid_preferred_location?: string[]
    invalid_preferred_press?: string[]
    invalid_single_sided_or_double_sided?: string[]
    invalid_product_sourcing?: string[]
    invalid_added_to_websites?: string[]
    total_rows: number
    has_marketplace_column: boolean
  }>> {
    const formData = new FormData()
    formData.append('file', file)
    const axiosInstance = (apiService as any).api
    const response = await axiosInstance.post(
      `${this.endpoint}/import-csv-preview`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      }
    )
    return response.data
  }

  /**
   * Import products from CSV
   * Backend: POST /api/products/import-csv
   * skipParams: optional per-attribute skip lists (skip rows whose value is in the list for that attribute).
   */
  async importCsv(
    file: File,
    updateExisting: boolean = true,
    skipParams: {
      marketplace?: string[]
      tree_note?: string[]
      product_sourcing?: string[]
      added_to_websites?: string[]
      finishing?: string[]
      finishing_reference?: string[]
      fold_catalogue?: string[]
      section_finishing?: string[]
      session?: string[]
      standard_or_custom?: string[]
      design_location?: string[]
      status_for_qp?: string[]
      signagent_status?: string[]
      status?: string[]
      preferred_location?: string[]
      preferred_press?: string[]
      single_sided_or_double_sided?: string[]
    } = {},
    nullParams: {
      standard_or_custom?: string[]
      design_location?: string[]
      status_for_qp?: string[]
      signagent_status?: string[]
      status?: string[]
      preferred_location?: string[]
      preferred_press?: string[]
      single_sided_or_double_sided?: string[]
      added_to_websites?: string[]
    } = {}
  ): Promise<ApiResponse<{
    created: number
    updated: number
    skipped: number
    errors: string[]
  }>> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('update_existing', String(updateExisting))
    const appendSkip = (key: string, values: string[]) => {
      values.forEach((v) => formData.append(`${key}[]`, v))
    }
    if (skipParams.marketplace?.length) appendSkip('skip_rows_with_marketplace', skipParams.marketplace)
    if (skipParams.tree_note?.length) appendSkip('skip_rows_with_tree_note', skipParams.tree_note)
    if (skipParams.added_to_websites?.length) appendSkip('skip_rows_with_added_to_websites', skipParams.added_to_websites)
    if (skipParams.product_sourcing?.length) appendSkip('skip_rows_with_product_sourcing', skipParams.product_sourcing)
    if (skipParams.finishing?.length) appendSkip('skip_rows_with_finishing', skipParams.finishing)
    if (skipParams.finishing_reference?.length) appendSkip('skip_rows_with_finishing_reference', skipParams.finishing_reference)
    if (skipParams.fold_catalogue?.length) appendSkip('skip_rows_with_fold_catalogue', skipParams.fold_catalogue)
    if (skipParams.section_finishing?.length) appendSkip('skip_rows_with_section_finishing', skipParams.section_finishing)
    if (skipParams.session?.length) appendSkip('skip_rows_with_session', skipParams.session)
    if (skipParams.standard_or_custom?.length) appendSkip('skip_rows_with_standard_or_custom', skipParams.standard_or_custom)
    if (skipParams.design_location?.length) appendSkip('skip_rows_with_design_location', skipParams.design_location)
    if (skipParams.status_for_qp?.length) appendSkip('skip_rows_with_status_for_qp', skipParams.status_for_qp)
    if (skipParams.signagent_status?.length) appendSkip('skip_rows_with_signagent_status', skipParams.signagent_status)
    if (skipParams.status?.length) appendSkip('skip_rows_with_status', skipParams.status)
    if (skipParams.preferred_location?.length) appendSkip('skip_rows_with_preferred_location', skipParams.preferred_location)
    if (skipParams.preferred_press?.length) appendSkip('skip_rows_with_preferred_press', skipParams.preferred_press)
    if (skipParams.single_sided_or_double_sided?.length) appendSkip('skip_rows_with_single_sided_or_double_sided', skipParams.single_sided_or_double_sided)

    const appendNull = (key: string, values: string[]) => {
      values.forEach((v) => formData.append(`${key}[]`, v))
    }
    if (nullParams.standard_or_custom?.length) appendNull('null_values_standard_or_custom', nullParams.standard_or_custom)
    if (nullParams.design_location?.length) appendNull('null_values_design_location', nullParams.design_location)
    if (nullParams.status_for_qp?.length) appendNull('null_values_status_for_qp', nullParams.status_for_qp)
    if (nullParams.signagent_status?.length) appendNull('null_values_signagent_status', nullParams.signagent_status)
    if (nullParams.status?.length) appendNull('null_values_status', nullParams.status)
    if (nullParams.preferred_location?.length) appendNull('null_values_preferred_location', nullParams.preferred_location)
    if (nullParams.preferred_press?.length) appendNull('null_values_preferred_press', nullParams.preferred_press)
    if (nullParams.single_sided_or_double_sided?.length)
      appendNull('null_values_single_sided_or_double_sided', nullParams.single_sided_or_double_sided)
    if (nullParams.added_to_websites?.length) appendNull('null_values_added_to_websites', nullParams.added_to_websites)

    const axiosInstance = (apiService as any).api
    const response = await axiosInstance.post(
      `${this.endpoint}/import-csv`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 1800000 // 30 minutes for large file uploads (5GB) in milliseconds
      }
    )

    return response.data
  }

  /**
   * Check if a product file (image or asset) exists
   * Used to prevent 404 navigation when clicking on missing files
   */
  async checkFileExists(
    productUuid: string,
    url: string,
    type?: 'image' | 'asset'
  ): Promise<{ exists: boolean; url: string }> {
    const params = new URLSearchParams({ url })
    if (type) {
      params.append('type', type)
    }
    const response = await apiService.get<{ exists: boolean; url: string }>(
      `${this.endpoint}/${productUuid}/files/check?${params.toString()}`
    )
    return response
  }

  /**
   * Bulk import media files (images and assets) for multiple products
   * Files are matched by SKU from the filename: extension removed, optional EVT_ prefix stripped,
   * then substring before the first _, space, or hyphen if any; otherwise the whole basename is the SKU.
   * 
   * @param files Array of File objects
   * @param onProgress Optional progress callback
   * @returns Promise with import results
   */
  /**
   * Sync import: POST import-media with files[] (use when ≤ IMPORT_MEDIA_SYNC_MAX_FILES).
   * On 422 "too many files", the modal should switch to queued flow (build ZIP, call importProductMediaQueued).
   */
  async importProductMedia(
    files: File[],
    onProgress?: (progress: number) => void,
    workflow: ImportMediaWorkflow = 'mixed',
    deleteOldImages = false
  ): Promise<ApiResponse<ImportMediaResult>> {
    const formData = new FormData()
    formData.append('workflow', workflow)
    if (deleteOldImages) {
      formData.append('delete_old_images', '1')
    }
    files.forEach((file) => {
      formData.append('files[]', file)
    })

    const axiosInstance = (apiService as any).api
    const response = await axiosInstance.post(
      `${this.endpoint}/import-media`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 1800000, // 30 minutes for bulk media imports
        onUploadProgress: (progressEvent: any) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            onProgress(progress)
          }
        }
      }
    )

    const responseData = response.data
    const results = responseData?.data || responseData
    return { data: results, status: response.status }
  }

  /**
   * Queued import: POST import-media/queued with a single ZIP file.
   * Returns 202 with import_media_id and status_url to poll, or 200 with status 'completed' and data when sync queue completes immediately.
   */
  async importProductMediaQueued(
    zipFile: File,
    onProgress?: (progress: number) => void,
    workflow: ImportMediaWorkflow = 'mixed',
    deleteOldImages = false
  ): Promise<ImportMediaQueuedResponse | ImportMediaQueuedCompletedResponse> {
    const formData = new FormData()
    formData.append('file', zipFile)
    formData.append('workflow', workflow)
    if (deleteOldImages) {
      formData.append('delete_old_images', '1')
    }

    const axiosInstance = (apiService as any).api
    const response = await axiosInstance.post(
      `${this.endpoint}/import-media/queued`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 1800000, // 30 min for large ZIP (max 512 MB)
        onUploadProgress: (progressEvent: any) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            onProgress(progress)
          }
        }
      }
    )

    const data = response.data
    if (response.status === 202) {
      return data as ImportMediaQueuedResponse
    }
    if (response.status === 200 && data?.status === 'completed') {
      return data as ImportMediaQueuedCompletedResponse
    }
    return data as ImportMediaQueuedResponse
  }

  /**
   * Poll import status: GET import-media/status/{importMediaId}.
   * Stop when status is 'completed', 'failed', or 'not_found'.
   */
  async getImportMediaStatus(importMediaId: string): Promise<ImportMediaStatusResponse> {
    const response = await apiService.get<ImportMediaStatusResponse>(
      `${this.endpoint}/import-media/status/${importMediaId}`
    )
    return response
  }

  /**
   * Per-product activity timeline (paginated with cursor).
   */
  async getProductEvents(
    productUuid: string,
    params?: { limit?: number; cursor?: string }
  ): Promise<ProductTimelineResponse> {
    const q = new URLSearchParams()
    if (params?.limit != null) q.set('limit', String(params.limit))
    if (params?.cursor) q.set('cursor', params.cursor)
    // Bust browser/CDN caches on refetch (e.g. after PIM media import).
    q.set('_t', String(Date.now()))
    const qs = q.toString()
    try {
      return await apiService.get<ProductTimelineResponse>(
        `${this.endpoint}/${productUuid}/events?${qs}`,
        { headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' } }
      )
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message || 'Failed to load timeline'
      return { success: false, events: [], message: msg, error: msg }
    }
  }

  /**
   * Global product activity across all products.
   */
  async getGlobalProductEvents(params?: {
    limit?: number
    cursor?: string
    search?: string
    page?: number
  }): Promise<ProductTimelineResponse> {
    const q = new URLSearchParams()
    if (params?.limit != null) q.set('limit', String(params.limit))
    if (params?.cursor) q.set('cursor', params.cursor)
    if (params?.search != null && params.search.trim() !== '') {
      q.set('search', params.search.trim().slice(0, 120))
    }
    if (params?.page != null && params.page >= 1) q.set('page', String(params.page))
    q.set('_t', String(Date.now()))
    const qs = q.toString()
    try {
      return await apiService.get<ProductTimelineResponse>(
        `${this.endpoint}/events/timeline?${qs}`,
        { headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' } }
      )
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message || 'Failed to load timeline'
      return { success: false, events: [], message: msg, error: msg }
    }
  }

  /**
   * Download global product timeline as CSV (streamed). Requires pim.products.view.
   */
  async exportGlobalProductTimelineCsv(filters?: { created_from?: string; created_to?: string }): Promise<void> {
    const qs = new URLSearchParams()
    if (filters?.created_from) qs.set('created_from', filters.created_from)
    if (filters?.created_to) qs.set('created_to', filters.created_to)
    qs.set('timezone', getUserTimezone())
    const q = qs.toString()
    await this.downloadAuthenticatedCsv(
      `${this.endpoint}/timeline/export?${q}`,
      'product-timeline-export.csv'
    )
  }

  /**
   * Download a single product timeline as CSV (streamed). Requires pim.products.view.
   */
  async exportProductTimelineCsv(
    productUuid: string,
    filters?: { created_from?: string; created_to?: string }
  ): Promise<void> {
    const qs = new URLSearchParams()
    if (filters?.created_from) qs.set('created_from', filters.created_from)
    if (filters?.created_to) qs.set('created_to', filters.created_to)
    qs.set('timezone', getUserTimezone())
    const q = qs.toString()
    await this.downloadAuthenticatedCsv(
      `${this.endpoint}/${encodeURIComponent(productUuid)}/timeline/export?${q}`,
      'product-timeline-export.csv'
    )
  }

  private parseFilenameFromContentDisposition(header: string | null, fallback: string): string {
    if (!header) return fallback
    const star = /filename\*=UTF-8''([^;\s]+)/i.exec(header)
    if (star?.[1]) {
      try {
        return decodeURIComponent(star[1].replace(/"/g, ''))
      } catch {
        /* fall through */
      }
    }
    const quoted = /filename="([^"]+)"/i.exec(header)
    if (quoted?.[1]) return quoted[1]
    const plain = /filename=([^;\s]+)/i.exec(header)
    if (plain?.[1]) return plain[1].replace(/^["']|["']$/g, '')
    return fallback
  }

  private async downloadAuthenticatedCsv(relativePath: string, fallbackFilename: string): Promise<void> {
    const token =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token')
        : null
    if (!token) {
      throw new Error('Authentication required')
    }
    const base = resolveApiBaseUrl()
    const path = relativePath.startsWith('/') ? relativePath : `/${relativePath}`
    const url = `${base}${path}`
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'text/csv',
      },
    })
    if (!response.ok) {
      const body = await response.text()
      let msg = `Export failed (${response.status})`
      try {
        const j = JSON.parse(body) as { message?: string }
        if (j?.message) msg = j.message
      } catch {
        if (body) msg = body.slice(0, 240)
      }
      throw new Error(msg)
    }
    const cd = response.headers.get('Content-Disposition')
    const filename = this.parseFilenameFromContentDisposition(cd, fallbackFilename)
    const blob = await response.blob()
    const objectUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = objectUrl
    link.download = filename
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    setTimeout(() => {
      document.body.removeChild(link)
      window.URL.revokeObjectURL(objectUrl)
    }, 100)
  }

  /**
   * Shared saved filter lists (PIM Products). Requires pim.products.view.
   */
  async listProductSavedFilters(params?: { q?: string; limit?: number }): Promise<ProductSavedFilterApi[]> {
    const res = await apiService.get<{ data: ProductSavedFilterApi[] }>(`${this.endpoint}/saved-filters`, {
      params: {
        q: params?.q,
        limit: params?.limit ?? 200,
      },
    })
    return Array.isArray(res?.data) ? res.data : []
  }

  /** Requires pim.products.save_filters */
  async createProductSavedFilter(body: {
    name: string
    payload: ProductSavedFilterPayload
  }): Promise<ProductSavedFilterApi> {
    const res = await apiService.post<{ data: ProductSavedFilterApi }>(`${this.endpoint}/saved-filters`, body)
    return res.data
  }

  /** Requires pim.products.save_filters */
  async updateProductSavedFilter(
    uuid: string,
    body: { name?: string; payload?: ProductSavedFilterPayload }
  ): Promise<ProductSavedFilterApi> {
    const res = await apiService.put<{ data: ProductSavedFilterApi }>(
      `${this.endpoint}/saved-filters/${uuid}`,
      body
    )
    return res.data
  }

  /**
   * Find a product by exact SKU for Universal Intake Edit PIM (scoped attribute snapshot).
   */
  async lookupProductBySku(sku: string): Promise<PIMProduct | null> {
    const q = sku.trim()
    if (!q) return null
    try {
      const body = await apiService.get<{ data: PIMProduct | null }>(`${this.endpoint}/intake-by-sku`, {
        params: { sku: q }
      })
      return body.data ?? null
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 404) return null
      throw err
    }
  }
}

export const productService = new ProductService()
export default productService

